import React, { useEffect, useRef } from 'react';
import { Paper } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { observer } from 'mobx-react';
import { isAlive } from 'mobx-state-tree';
import { ErrorBoundary } from 'react-error-boundary';
import { getConf } from '@jbrowse/core/configuration';
import { ResizeHandle, ErrorMessage } from '@jbrowse/core/ui';
import { useDebouncedCallback } from '@jbrowse/core/util';
import TrackLabel from './TrackLabel';
var useStyles = makeStyles()({
  root: {
    marginTop: 2
  },
  resizeHandle: {
    height: 3,
    boxSizing: 'border-box',
    position: 'relative',
    zIndex: 2
  },
  overlay: {
    pointerEvents: 'none',
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    zIndex: 3
  },
  trackLabel: {
    zIndex: 3
  },
  // aligns with block boundaries. check for example the breakpoint split view
  // demo to see if features align if wanting to change things
  renderingComponentContainer: {
    position: 'absolute',
    // -1 offset because of the 1px border of the Paper
    left: -1,
    height: '100%',
    width: '100%'
  },
  trackLabelOffset: {
    position: 'relative',
    display: 'inline-block'
  },
  trackLabelOverlap: {
    position: 'absolute'
  },
  trackRenderingContainer: {
    overflowY: 'auto',
    overflowX: 'hidden',
    whiteSpace: 'nowrap',
    position: 'relative',
    background: 'none',
    zIndex: 2
  }
});
var TrackContainerLabel = observer(function (_ref) {
  var model = _ref.model,
    view = _ref.view;
  var _useStyles = useStyles(),
    classes = _useStyles.classes,
    cx = _useStyles.cx;
  var display = model.displays[0];
  var trackLabel = classes.trackLabel,
    trackLabelOverlap = classes.trackLabelOverlap,
    trackLabelOffset = classes.trackLabelOffset;
  var labelStyle = view.trackLabels !== 'overlapping' || display.prefersOffset ? trackLabelOffset : trackLabelOverlap;
  return view.trackLabels !== 'hidden' ? React.createElement(TrackLabel, {
    track: model,
    className: cx(trackLabel, labelStyle)
  }) : null;
});
function TrackContainer(_ref2) {
  var model = _ref2.model,
    track = _ref2.track;
  var _useStyles2 = useStyles(),
    classes = _useStyles2.classes;
  var display = track.displays[0];
  var horizontalScroll = model.horizontalScroll,
    draggingTrackId = model.draggingTrackId,
    moveTrack = model.moveTrack;
  var height = display.height,
    RenderingComponent = display.RenderingComponent,
    DisplayBlurb = display.DisplayBlurb;
  var trackId = getConf(track, 'trackId');
  var ref = useRef(null);
  var dimmed = draggingTrackId !== undefined && draggingTrackId !== display.id;
  var minimized = track.minimized;
  var debouncedOnDragEnter = useDebouncedCallback(function () {
    if (isAlive(display) && dimmed) {
      moveTrack(draggingTrackId, track.id);
    }
  }, 100);
  useEffect(function () {
    if (ref.current) {
      model.trackRefs[trackId] = ref.current;
    }
    return function () {
      delete model.trackRefs[trackId];
    };
  }, [model.trackRefs, trackId]);
  return React.createElement(Paper, {
    className: classes.root,
    variant: "outlined"
  }, React.createElement(TrackContainerLabel, {
    model: track,
    view: model
  }), React.createElement(ErrorBoundary, {
    key: track.id,
    FallbackComponent: function FallbackComponent(_ref3) {
      var error = _ref3.error;
      return React.createElement(ErrorMessage, {
        error: error
      });
    }
  }, React.createElement("div", {
    className: classes.trackRenderingContainer,
    style: {
      height: minimized ? 20 : height
    },
    onScroll: function onScroll(evt) {
      return display.setScrollTop(evt.currentTarget.scrollTop);
    },
    onDragEnter: debouncedOnDragEnter,
    "data-testid": "trackRenderingContainer-".concat(model.id, "-").concat(trackId)
  }, !minimized ? React.createElement(React.Fragment, null, React.createElement("div", {
    ref: ref,
    className: classes.renderingComponentContainer,
    style: {
      transform: "scaleX(".concat(model.scaleFactor, ")")
    }
  }, React.createElement(RenderingComponent, {
    model: display,
    onHorizontalScroll: horizontalScroll
  })), DisplayBlurb ? React.createElement("div", {
    style: {
      position: 'absolute',
      left: 0,
      top: display.height - 20
    }
  }, React.createElement(DisplayBlurb, {
    model: display
  })) : null) : null)), React.createElement("div", {
    className: classes.overlay,
    style: {
      height: display.height,
      background: dimmed ? 'rgba(0, 0, 0, 0.4)' : undefined
    },
    onDragEnter: debouncedOnDragEnter
  }), React.createElement(ResizeHandle, {
    onDrag: display.resizeHeight,
    className: classes.resizeHandle
  }));
}
export default observer(TrackContainer);